import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import injectSheet from 'react-jss';
import { isEqual } from 'lodash';
import withSingleFieldValidation from '../../hocs/withSingleFieldValidation';
import UploadList from '../../components/UploadList';
import Input from '../../components/Upload/Input';
import FormMessage from '../../components/FormMessage';
import { addFileAction, removeFileAction } from '../../actions/uploadFile';
import { fileUploadProps } from '../../models/panels';
import styles from './styles.jss';

class FileUpload extends React.Component {
  constructor() {
    super();
    this.state = {
      // Uwaga: `error` może być:
      // - kluczem lokalizacyjnym (np. `${localePath}.error-file-size`) — wtedy renderujemy <FormMessage />
      // - tekstem prostym z lokalnej walidacji (np. "Ungültiges Dateiformat.") — wtedy pokazujemy zwykły <div>
      error: '',
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const { data: previousData } = prevProps;
    const { data: currentData, getValidationProps, setNavigationState } = this.props;
    const { name, onChange } = getValidationProps();

    // zsynchronizuj pole formularza (jak było wcześniej)
    if (!isEqual(previousData, currentData)) {
      const target = { name, value: currentData };
      onChange({ target });
    }

    // jeżeli zmienił się stan błędu, zaktualizuj blokadę Next
    if (prevState.error !== this.state.error && typeof setNavigationState === 'function') {
      setNavigationState({ isNextDisabled: !!this.state.error });
    }
  }

  // —— WALIDACJE BACKEND/REGUŁY DOTYCHCZASOWE ——
  validFileSize = (size) => {
    const { validationTypes } = fileUploadProps;
    const fileSizeMB = (size / 1024 / 1024).toFixed(validationTypes.ACCEPTED_FILE_SIZE);
    return fileSizeMB <= validationTypes.ACCEPTED_FILE_SIZE;
  };

  validFileName = (name) => (
    !fileUploadProps.validationTypes.DISALLOWED_FILENAME_CHARS.test(name)
    && name.length < fileUploadProps.validationTypes.MAXIMUM_FILENAME_LENGTH
  );

  validMaxFiles = (lengthFiles) =>
    lengthFiles < fileUploadProps.validationTypes.UPLOADED_FILES_NUMBER_LIMIT;

  // ustawia błąd + (po componentDidUpdate) zablokuje Weiter
  catchError = (path) => {
    const { localePath } = this.props;
    const messageKey = path ? `${localePath}.${path}` : '';
    this.setState({ error: messageKey });
  };

  // —— AKCJE USUWANIA/DODAWANIA (jak było) ——
  async deleteFile(fileId) {
    const { getValidationProps, removeFile, pathName } = this.props;
    const { name } = getValidationProps();
    const message = await removeFile(fileId, name, pathName); // message = klucz bez prefiksu
    this.catchError(message); // zamieni na pełny klucz lub wyczyści
  }

  async addFile(file) {
    const { pathName, getValidationProps, addFile } = this.props;

    if (!this.validFileName(file.name)) {
      this.catchError('error-invalid-filename');
      return false;
    }

    if (!this.validFileSize(file.size)) {
      this.catchError('error-file-size');
      return false;
    }

    const { name } = getValidationProps();
    const formData = new FormData();
    formData.append('attachment', file);

    const message = await addFile(formData, name, pathName); // message = klucz lub ''
    this.catchError(message);
    return !message; // true, jeśli brak błędu z backendu
  }

  // przyjmij listę DOBRYCH plików z Input i dodaj po kolei
  addManyFiles = async (files) => {
    if (!files || files.length === 0) return;

    const MAX = fileUploadProps.validationTypes.UPLOADED_FILES_NUMBER_LIMIT; // 9
    const already = this.props.data.length;
    const remaining = Math.max(0, MAX - already);

    // zamroź listę wejściową i przytnij do pozostałego limitu
    const filesArr = Array.from(files);
    const filesCapped = filesArr.slice(0, remaining);

    if (filesArr.length > remaining) {
      // prosta, tekstowa informacja (nie-i18n), żeby użytkownik wiedział czemu reszta nie weszła
      this.handleValidationError(`Maximale Anzahl von Dateien: ${MAX}.`);
    }

    // Dodaj sekwencyjnie
    for (let i = 0; i < filesCapped.length; i += 1) {
      const ok = await this.addFile(filesCapped[i]);
      if (!ok) break;
      // bezpieczeństwo: jeśli w międzyczasie osiągnęliśmy limit — przerwij
      if ((this.props.data.length + (i + 1)) >= MAX) break;
    }

    // jeśli nie mamy klucza i18n z backendu — wyczyść lokalny błąd
    if (!this.state.error || !this.state.error.startsWith(this.props.localePath)) {
      this.setState({ error: '' });
    }
  };

  // callback z Input: błąd walidacji po stronie frontu (format/size/magic)
  handleValidationError = (msg) => {
    // Tekst (np. "Ungültiges Dateiformat." / "Datei ist größer als 5 MB.") — blokuje „Weiter”
    this.setState({ error: msg || '' });
  };

  render() {
    const { name, data, accept, localePath, classes } = this.props;
    const { t } = this.context;
    const { error } = this.state;

    // czy błąd wygląda na klucz i18n pochodzący z backendu/akcji (ma prefiks localePath)?
    const isI18nError = !!error && error.startsWith(localePath);

    return (
      <div className="esc_grid">
        <div className={`esc_grid__wrapper ${classes.wrapper}`}>
          {/* Lista już dodanych plików */}
          <UploadList
            files={data}
            accept={accept}
            namespace={name}
            onRemove={(fileId) => this.deleteFile(fileId)}
          />

          {/* Slot do dodawania nowego pliku */}
          {this.validMaxFiles(data.length) && (
            <div className="esc_col-12 esc_col-s-6 esc_col-m-4 esc_col-l-4">
              <Input
                name={name}
                accept={accept}
                text={t(`${localePath}.upload-label`)}
                // ⬇️ Input wywołuje onChange(goodFiles[]) zamiast eventu
                onChange={(goodFiles) => this.addManyFiles(goodFiles)}
                // ⬇️ Błąd walidacji z Input blokuje „Weiter”
                onValidationError={this.handleValidationError}
              />

              {/* Komunikat błędu */}
              {error && (
                <div className={classes.error}>
                  {isI18nError ? (
                    <FormMessage error={error} />
                  ) : (
                    // tekstowy błąd z lokalnej walidacji (Input)
                    <div>{error}</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

FileUpload.propTypes = {
  ...fileUploadProps.props,
  // PanelProvider zwykle to podaje; robimy opcjonalne, by nie wywalało gdy brak
  setNavigationState: PropTypes.func,
};

FileUpload.defaultProps = {
  ...fileUploadProps.defaults,
  setNavigationState: undefined,
};

FileUpload.contextTypes = {
  t: PropTypes.func,
};

const mapStateToProps = ({ formData }, ownProps) => ({
  data: formData[ownProps.pathName][ownProps.name],
});

const mapDispatchToProps = (dispatch) => ({
  addFile: (data, name, pathName) => dispatch(addFileAction(data, name, pathName)),
  removeFile: (fileId, name, pathName) => dispatch(removeFileAction(fileId, name, pathName)),
});

export { FileUpload as FileUploadPlain };
export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withSingleFieldValidation,
  injectSheet(styles),
)(FileUpload);
